import React, { useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from 'components/common/Button';
import CloseIcon from '@material-ui/icons/Close';
import Form from 'components/form';
import DateInput from 'components/form/DateInput';
import InputLabel from '@material-ui/core/InputLabel';
import { useMutation } from '@apollo/react-hooks';
import useSnack from 'hooks/useSnack';
import { UPDATE_DOCUMENT } from 'graphql/student/essays';
import PRODUCTS from 'constant/product-types';

const FormItem = Form.FormItem;

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const useStyles = makeStyles((theme) => ({
  label: {
    display: 'block',
    position: 'initial',
    transform: 'scale(.9)',
    fontWeight: 400,
    color: '#626C82',
    marginBottom: theme.spacing(1),
  },
  dialogTitle: {
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.dialogTitle}
      {...other}
    >
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DueDateModal = ({ onClose, form, doc, student }) => {
  const classes = useStyles();
  const snack = useSnack();
  const [updateDocument] = useMutation(UPDATE_DOCUMENT);
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then((values) => {
      if (doc) {
        return updateDocument({
          variables: {
            id: doc.id,
            data: {
              ...values,
              status: doc.status,
              draft_due_date: moment(values.draft_due_date).format(
                'YYYY-MM-DD',
              ),
            },
          },
        }).then(
          () => {
            window.analytics.track('Due date assigned to essay draft', {
              id: doc.id,
              ...values,
              status: doc.status,
              draft_due_date: moment(values.draft_due_date).format(
                'YYYY-MM-DD',
              ),
              product: PRODUCTS_LOOKUP[student.product_type]
             });
            snack.success('Document updated');
            onClose();
          },
          (e) => snack.error(e.message),
        );
      }
    });
  };

  const handleReset = () => {
    return updateDocument({
      variables: {
        id: doc.id,
        data: {
          status: doc.status,
          draft_due_date: null,
        },
      },
    }).then(
      () => {
        snack.success('Document updated');
        onClose();
      },
      (e) => snack.error(e.message),
    );
  };

  return (
    <Dialog open fullWidth maxWidth={'sm'} onClose={onClose}>
      <DialogTitle
        onClose={onClose}
      >{`Please set the due date for the student’s next draft.`}</DialogTitle>
      <DialogContent>
        <Form>
          <Grid
            container
            spacing={3}
            style={{ justifyContent: 'center', textAlign: 'center' }}
          >
            <Grid item>
              <InputLabel disableAnimation shrink className={classes.label}>
                Due Date
              </InputLabel>
              <FormItem>
                {getFieldDecorator('draft_due_date', {
                  initialValue: doc?.draft_due_date,
                  rules: [{ required: true, message: 'Required' }],
                })(<DateInput inputVariant="outlined" size="small" />)}
              </FormItem>
            </Grid>
          </Grid>
        </Form>
        <Box
          display="flex"
          justifyContent="space-evenly"
          textAlign="right"
          marginTop={3}
          paddingBottom={2}
        >
          <Button
            size="large"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
          >
            {`Submit`}
          </Button>
          <Button size="large" color="secondary" onClick={handleReset}>
            {`No Due Date`}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Form.create()(DueDateModal);
